// AppData.js — the "App data" dropdown in the note tab. Read-only.
//
// `appContext` is the visit's own `app_context` field — what connected apps set
// for this patient, keyed by app id. Shown whenever there is any, independent
// of the clinician's Clinical Facts toggle: one collapsible row, each app's
// section inside — logo, name, when it arrived — rendered as markdown
// (react-markdown + GFM, the same renderer Chat uses). Deliberately not
// editable: the app's latest setContext always wins, so there is nothing to
// merge and no edit to lose. Clinicians who disagree with an app's data write
// their own Clinical Facts; the note sees both.
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import useVendorStore from '../../utils/vendorApps/useVendorStore';

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

function AppSection({ section, app }) {
  return (
    <div className="mb-3" data-app-facts={section.app}>
      <div className="d-flex align-items-center small mb-1">
        {app?.icon && <img src={app.icon} alt="" width="16" height="16" className="rounded mr-1" style={{ objectFit: 'cover' }} />}
        <strong style={{ color: app?.color || undefined }}>{section.app}</strong>
        {section.about && <span className="text-muted ml-1">· {section.about}</span>}
        {section.when && <span className="text-muted ml-2">provided {section.when}</span>}
      </div>
      <div className="app-data-markdown small px-2 py-1 rounded" style={{ background: '#fff', border: '1px solid #e9ecef' }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.markdown}</ReactMarkdown>
      </div>
    </div>
  );
}

export default function AppData({ appContext }) {
  const store = useVendorStore();
  const [open, setOpen] = useState(false);
  const sections = Object.entries(appContext || {})
    .filter(([, e]) => e && String(e.markdown || '').trim())
    .map(([id, e]) => ({ id, app: e.name || 'app', markdown: e.markdown, when: fmtTime(e.providedAt), providedAt: e.providedAt }))
    // What apps say about the clinician (chat reads it from the user record;
    // notes never do). Shown here so we can see what the app is telling us.
    .concat(store.providerBuffersForVisit().map((b) => ({ id: `${b.appId}#provider`, app: b.app, about: 'about you', markdown: b.markdown, when: fmtTime(b.providedAt), providedAt: b.providedAt })))
    .sort((a, b) => String(a.providedAt || '').localeCompare(String(b.providedAt || '')));
  if (sections.length === 0) return null;
  const latest = sections.map((s) => s.when).filter(Boolean).pop();

  return (
    <div className="outlineContainer" data-app-data>
      <div className="flex-container" style={{ display: 'flex', alignItems: 'center' }}>
        <strong className="dropdown-label" onClick={() => setOpen(!open)} style={{ flex: 1, marginRight: '10px', display: 'flex', alignItems: 'center' }}>
          <div className={`arrow ${open ? 'open' : ''}`}></div>
          <div style={{ fontSize: '0.85rem' }}>App data</div>
          <small className="text-muted ml-2" style={{ fontWeight: 400 }}>
            {sections.length === 1 ? sections[0].app : `${sections.length} apps`}
            {latest ? ` · ${latest}` : ''}
          </small>
        </strong>
      </div>
      {open && (
        <div className="pt-2">
          {sections.map((s) => (
            <AppSection key={s.id} section={s} app={store.getApp(s.id) || store.apps.find((a) => a.name === s.app) || null} />
          ))}
        </div>
      )}
    </div>
  );
}
